import React from 'react';
import PropTypes from 'prop-types';
import { UPLOAD_DOC_PIC } from '../queries';
import { useMutation } from 'react-apollo';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Loader from './Loader';
import { displayError } from '../utilities';
import { GET_DOCTOR } from '../pages/Doctors';

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/jpg'];
const MIN_FILE_SIZE = 10 * 1024; // 10KB
const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
const MIN_DIMENSION = 400;

const styles = {
  img: { border: '3px solid white', borderRadius: '50%', objectFit: 'cover' },
  fileInput: { visibility: 'hidden', position: 'absolute' },
  loader: {
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 1
  }
};

const validationSchema = Yup.object().shape({
  imageFile: Yup.mixed()
    .test(
      'fileType',
      'Only JPG and PNG images are allowed',
      value => !value || ALLOWED_TYPES.includes(value.type)
    )
    .test(
      'fileSize',
      'Image size should be between 10KB and 2MB',
      value =>
        !value || (value.size >= MIN_FILE_SIZE && value.size <= MAX_FILE_SIZE)
    )
    .test(
      'resolution',
      `Image resolution should be HD quality (at least ${MIN_DIMENSION}x${MIN_DIMENSION})`,
      async value => {
        if (!value) return true;
        return new Promise(resolve => {
          const url = URL.createObjectURL(value);
          const img = new Image();
          img.onload = () => {
            URL.revokeObjectURL(url);

            console.log({
              w: img.width,
              h: img.height
            });
            resolve(img.width >= MIN_DIMENSION && img.height >= MIN_DIMENSION);
          };
          img.onerror = () => {
            URL.revokeObjectURL(url);
            resolve(false);
          };
          img.src = url;
        });
      }
    )
});

const UploadDP = ({ url, doctorId }) => {
  const [uploadDP, { loading }] = useMutation(UPLOAD_DOC_PIC, {
    refetchQueries: [{ query: GET_DOCTOR, variables: { id: doctorId } }]
  });

  const formik = useFormik({
    initialValues: { imageFile: null },
    validationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        await uploadDP({
          variables: { imageFile: values.imageFile, doctorId }
        });
      } catch (error) {
        displayError(error);
        setDisplayPic(url);
      } finally {
        setSubmitting(false);
        resetForm();
      }
    }
  });

  const { setFieldValue, validateForm, submitForm } = formik;

  const [displayPic, setDisplayPic] = React.useState(url);

  React.useEffect(() => {
    setDisplayPic(url);
  }, [url]);

  const handleFileChange = async event => {
    const file = event.currentTarget.files[0];
    if (!file) return;

    const previewUrl = URL.createObjectURL(file);
    setDisplayPic(previewUrl);

    await setFieldValue('imageFile', file);
    const validationErrors = await validateForm();

    if (validationErrors.imageFile) {
      displayError(validationErrors.imageFile);
      setDisplayPic(url);
    } else {
      submitForm();
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      <label htmlFor="image" style={{ cursor: 'pointer', margin: 0 }}>
        <img
          src={displayPic}
          height={150}
          width={150}
          style={styles.img}
          alt="Selected Doctor"
        />
        <input
          type="file"
          id="image"
          accept={ALLOWED_TYPES.join(',')}
          style={styles.fileInput}
          onChange={handleFileChange}
        />
        <div className="btn btn-primary btn-xs upload-dp">CHANGE</div>
      </label>
      {loading && (
        <div style={styles.loader}>
          <Loader size={5} />
        </div>
      )}
    </div>
  );
};

UploadDP.propTypes = {
  url: PropTypes.string.isRequired,
  doctorId: PropTypes.string.isRequired
};

export default UploadDP;
